/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React from "react";
import dayjs from "dayjs";
import theme from "../style/theme";
import { ReferenceDot } from "recharts";
import { closest, toISOStringWithTimezone } from "./Util";
import {
  ALARM_SEVERITIES,
  getAlarmSeverityColor,
  getAlarmSeverityRank,
  normalizeAlarmSeverity,
} from "./AlarmColors";
const GraphUtil = {};

/**
 * Locate the index of the timeSeries row whose `unixTime` is closest to the
 * supplied value. Returns -1 when there is no matching row.
 */
GraphUtil.findIndexByClosestUnixTime = (timeSeries, unixTime) => {
  if (!timeSeries?.length) return -1;
  const unixTimes = timeSeries.map((row) => Number(row.unixTime)).filter((v) => Number.isFinite(v));
  if (!unixTimes.length) return -1;
  const closestUnix = closest(unixTimes, Number(unixTime));
  return timeSeries.findIndex((r) => Number(r.unixTime) === closestUnix);
};

/**
 * Locate the timeSeries row whose `unixTime` is closest to the supplied value.
 * Returns null when there is no matching row.
 */
GraphUtil.findRowByClosestUnixTime = (timeSeries, unixTime) => {
  const index = GraphUtil.findIndexByClosestUnixTime(timeSeries, unixTime);
  return index >= 0 ? timeSeries[index] : null;
};

/**
 * Build a synthetic recharts-style tooltip payload from a timeSeries by
 * looking up the closest row to `unixTime` and mapping it through
 * `buildEntries(row)`. Useful for driving a sibling chart's tooltip from a
 * companion strip (e.g. weather timeline) where the hovered chart doesn't
 * itself contain the series data we want to display.
 *
 * Returns null when no matching row exists or `buildEntries` returns no items.
 */
GraphUtil.buildSyntheticGraphPayload = (timeSeries, unixTime, buildEntries) => {
  const row = GraphUtil.findRowByClosestUnixTime(timeSeries, unixTime);
  if (!row) return null;
  const entries = buildEntries(row) ?? [];
  return entries.length ? entries : null;
};

/**
 * Once we have the raw series data, we need to decorate it with some things. First, is the "big buckets".  This is the coarse grained bucket into which things like alarms fall.
 */
GraphUtil.calculateBigBuckets = (timeSeries) => {
  // if we dont' have data, we can't do anything
  if (!timeSeries?.length) return;

  let newBigBuckets = [];
  const bucketFactor = timeSeries.length >= 10 ? 10 : timeSeries.length;
  const middle = bucketFactor / 2;
  let index = 0;

  // Add null checks data that isn't sutible for buckets
  if (
    !timeSeries[middle]?.bucket ||
    !timeSeries[0]?.bucket ||
    !timeSeries[bucketFactor - 1]?.bucket
  ) {
    return { newBigBuckets: [], timeSeries };
  }

  let currentBucket = timeSeries[middle].bucket;
  let bucketStart = timeSeries[0].bucket;
  let bucketStop = timeSeries[bucketFactor - 1].bucket;

  timeSeries.forEach((bucket) => {
    // Skip if bucket is undefined or doesn't have the bucket property
    if (!bucket?.bucket) {
      return;
    }

    if (index === middle) {
      currentBucket = bucket.bucket;
    }
    if (index === 0) {
      bucketStart = bucket.bucket;
      // use stop of previous big bucket as start of next one, if it exists.
      // add one millisecond so they don't overlap
      if (newBigBuckets.length > 0) {
        let lastEnd = new Date(newBigBuckets[newBigBuckets.length - 1].stop).getTime() + 1;
        lastEnd = dayjs(lastEnd).format("YYYY-MM-DDTHH:mm:ss.SSS");
        bucketStart = lastEnd;
      }
    }
    if (index === bucketFactor - 1) {
      bucketStop = bucket.bucket;
    }
    bucket.bigBucket = currentBucket;
    index++;
    if (index === bucketFactor) {
      index = 0;
      newBigBuckets.push({
        start: bucketStart,
        stop: bucketStop,
        middle: currentBucket,
      });
    }
  });
  return { newBigBuckets, timeSeries };
};

// ~720 points per range. Presentation accepts sub-minute intervals as-is but
// rejects anything above 60 s that isn't a whole number of minutes, so ceil those.
GraphUtil.calculateDataInterval = (rangeMs) => {
  const seconds = Math.max(1, Math.round(rangeMs / 1000 / 720));
  return seconds < 60 ? seconds : Math.ceil(seconds / 60) * 60;
};

GraphUtil.calculateTickInterval = (range) => {
  let interval = 0;
  switch (range) {
    case "today":
      interval = 59;
      break;
    case "yesterday":
      interval = 59;
      break;
    case "7days":
      interval = 102;
      break;
    case "custom":
      interval = 59;
      break;
    default:
      interval = 102;
      break;
  }

  return interval;
};

GraphUtil.getTickFormatter = (displayRange) => {
  // if our time span is 24 hours or less, show time.  Otherwise, show date.
  let timeSpan = new Date(displayRange.stop).getTime() - new Date(displayRange.start).getTime();
  if (timeSpan <= 86400000) {
    return (bucketDate) => {
      let d = new Date(bucketDate);
      return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    };
  } else {
    return (bucketDate) => {
      let d = new Date(bucketDate);
      return d.toLocaleDateString("en-US", {
        dateStyle: "medium",
      });
    };
  }
};

GraphUtil.getAlarmTimelineArea = (areaProps) => {
  return (
    <rect
      x={areaProps.x}
      y={areaProps.y}
      width={areaProps.width}
      height={"20px"}
      fill={theme.palette.grey.light}
    />
  );
};

GraphUtil.getReferenceDot = (dotProps, bucketAlarms, isSelected = false, onClick) => {
  let severityColor = getAlarmSeverityColor(bucketAlarms.severity);
  let textColor = "#FFF";
  // Medium and Low are light enough that a white count would be unreadable.
  let labelColor = theme.palette.getContrastText(severityColor);
  const handleClick = onClick
    ? (e) => {
        e.stopPropagation();
        onClick();
      }
    : undefined;
  const dot = (
    <g style={{ cursor: "pointer" }} onClick={handleClick}>
      <circle
        style={{
          fill: bucketAlarms.resolved ? textColor : severityColor,
          stroke: bucketAlarms.resolved ? severityColor : textColor,
          strokeWidth: isSelected ? 2 : 1,
          strokeMiterlimit: 10,
          filter: isSelected ? "drop-shadow( 0px 4px 3px rgba(0, 0, 0, .35))" : "none",
        }}
        cx={dotProps.cx}
        cy={dotProps.cy}
        r="8.5"
      ></circle>
      <text
        fontSize={9}
        x={dotProps.cx}
        y={dotProps.cy}
        textAnchor="middle"
        alignmentBaseline="central"
        fill={bucketAlarms.resolved ? severityColor : labelColor}
        fontFamily="IBM Plex Sans, Arial, Helvetica, sans-serif"
        fontWeight="500"
      >
        {bucketAlarms.alarms.length}
      </text>
    </g>
  );
  return dot;
};

GraphUtil.getBucketAlarms = (bigBucket, alarmHistoryData) => {
  let bucketAlarms = [];

  // occaisionally, during load, we get a null big bucket.  if that happens, return empty array
  if (!bigBucket) return bucketAlarms;

  let bbStartTime = new Date(bigBucket.start).getTime();
  let bbStopTime = new Date(bigBucket.stop).getTime();
  let resolved = true;
  // The bucket shows one dot, so it takes the worst severity it contains.
  let severity = ALARM_SEVERITIES[ALARM_SEVERITIES.length - 1];
  if (alarmHistoryData?.historical_alerts) {
    alarmHistoryData.historical_alerts.forEach((alarm) => {
      let alarmTime = new Date(alarm.timestamp).getTime();
      if (bbStartTime <= alarmTime && bbStopTime >= alarmTime) {
        bucketAlarms.push(alarm);
        if (alarm.stop_time === null) {
          resolved = false;
        }
        if (getAlarmSeverityRank(alarm.alarm_type) < getAlarmSeverityRank(severity)) {
          severity = normalizeAlarmSeverity(alarm.alarm_type);
        }
      }
    });
  }
  return { severity: severity, resolved: resolved, alarms: bucketAlarms };
};

GraphUtil.getAlarmTimelineDots = (
  bigBuckets,
  alarmHistoryData,
  currentBigBucketTime,
  tooltipVisible,
  onDotClick,
) => {
  let refDots = [];
  bigBuckets.forEach((bb) => {
    let bucketAlarms = GraphUtil.getBucketAlarms(bb, alarmHistoryData);
    if (bucketAlarms.alarms.length > 0) {
      const selected = bb.middle === currentBigBucketTime && tooltipVisible;
      const handleClick = onDotClick ? () => onDotClick(bb, bucketAlarms) : undefined;
      refDots.push(
        <ReferenceDot
          r={8}
          key={bb.stop}
          stroke="none"
          y={65}
          x={bb.stop}
          shape={(dotProps) =>
            GraphUtil.getReferenceDot(dotProps, bucketAlarms, selected, handleClick)
          }
        />,
      );
    }
  });
  return refDots;
};

GraphUtil.getBucketAlarmsByMiddle = (targetBucket, bigBuckets, alarmHistoryData) => {
  const bb = bigBuckets.find((b) => b.middle === targetBucket);
  return GraphUtil.getBucketAlarms(bb, alarmHistoryData);
};

/** Generate a closure function that gets alarms from the selected bucket aggrigation.  We currently use this closure since the payload can change depending on what is hovered.
 
TODO We could avoid this closure and pass a simple array of selected alarms by attaching a hover event to the main graph and creating state to store the selected alarms.  This system would still need to be implemented in each graph (SitePowerGraph, TimeSeriesLineGraph and AlarmsTimelineGraph), so for now I have simply standardized the closure functions here.
*/
GraphUtil.generateGetBucketAlarms = (bigBuckets, alarmHistoryData) => {
  const getBucketAlarms = (payload) => {
    if (payload?.length === 0) return [];
    const bigBucket = payload[0].payload.bigBucket;
    const alarms = GraphUtil.getBucketAlarmsByMiddle(
      bigBucket,
      bigBuckets,
      alarmHistoryData,
    ).alarms;
    return alarms;
  };

  return getBucketAlarms;
};

GraphUtil.handleZoomStart = (ev, zoomParams, setZoomParams) => {
  // NTM - activeLabel property of the event used to be the date, now it appears to be an index value?
  // did something change in Recharts?
  if (ev.activePayload?.length) {
    let newParams = { ...zoomParams };
    newParams.zooming = true;
    newParams.start = ev.activePayload[0].payload.bucket;
    newParams.startIndex = ev.activeLabel;
    newParams.stop = ev.activePayload[0].payload.bucket;
    newParams.stopIndex = ev.activeLabel;
    setZoomParams(newParams);
  }
};

GraphUtil.handleZoomUpdate = (ev, zoomParams, setZoomParams) => {
  if (zoomParams.zooming && ev?.activePayload?.length) {
    let newParams = { ...zoomParams };
    newParams.stop = ev.activePayload[0].payload.bucket;
    newParams.stopIndex = ev.activeLabel;
    setZoomParams(newParams);
  }
};

// need to set the interval before we update the range
GraphUtil.updateDisplayRange = (range) => {
  // we have to calculate interval here becuase several things can affect the displayed range
  // when the range updates, the interval needs to update as well.
  const startTime = new Date(range.start).getTime();
  const stopTime = new Date(range.stop).getTime();
  range.interval = GraphUtil.calculateDataInterval(Math.abs(stopTime - startTime));
  return range;
};

/**
 * When the user releases the mouse, that signals the end of a zoom event.
 * set zooming to false, to hide the reference area that defines the zoom.
 * set the zoom stop time
 * update the display range, which triggers re-fetching data and re-rendering.
 * @param {*} ev
 */
GraphUtil.handleZoomEnd = (ev, zoomParams, setZoomParams, timezone, displayRange) => {
  if (ev.activePayload?.length) {
    // new params is the zoom parameters, start, stop, zooming, zoomed, etc
    let newParams = { ...zoomParams };
    newParams.zooming = false;
    newParams.zoomed = true;
    newParams.stop = ev.activePayload[0].payload.bucket;
    newParams.stopIndex = ev.activeLabel;
    setZoomParams(newParams);

    // new range is the calculated range used by the API
    let newRange = { ...displayRange };
    newRange.start = newParams.start;
    newRange.stop = newParams.stop;
    newRange.range = "custom";

    // if start and stop are different, then we can zoom!
    if (newParams.start !== newParams.stop) {
      //first, make sure that end is after start, if not reverse 'em.
      // this handles the right to left drag case.
      if (new Date(newParams.start).getTime() > new Date(newParams.stop).getTime()) {
        let swap = newParams.start;
        newRange.start = newRange.stop;
        newRange.stop = swap;
      }

      newRange.start = toISOStringWithTimezone(newRange.start, timezone);
      newRange.stop = toISOStringWithTimezone(newRange.stop, timezone);
      return newRange;
    }
  }
};

GraphUtil.getInitialGraphRange = (timezone) => {
  let start = new Date();
  let stop = new Date();
  start.setHours(0, 0, 0, 0);
  // use start of next day to stay consistent with other range update code
  stop.setHours(0, 0, 0, 0);
  stop.setDate(stop.getDate() + 1);
  return {
    range: "today",
    start: toISOStringWithTimezone(start, timezone),
    stop: toISOStringWithTimezone(stop, timezone),
    interval: 120,
  };
};

// Rolling 24-hour window ending at "now".
GraphUtil.getInitial24HourGraphRange = (timezone) => {
  let stop = new Date();
  let start = new Date();
  start.setHours(start.getHours() - 24);
  return {
    range: "24hours",
    start: dayjs(start).tz(timezone).format(),
    stop: dayjs(stop).tz(timezone).format(),
    interval: 120,
  };
};

export default GraphUtil;
